import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  getLanguageUrl,
  getLanguageFromUrl,
} from "../../utilities/languageUrls";
import {
  numberOfUuidsChanged,
  generateUuidsAsync,
  clearGeneratedUuids,
  selectState,
} from "./UuidGeneratorSlice";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import {
  Box,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,

} from "@mui/material";
import { CustomButton } from "../../components/custom_button/CustomButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { HomeCard } from "../../components/home_card/HomeCard";
import PasswordGeneratorImage from "../../assets/PasswordGenerator.webp";
import LoremIpsumGeneratorImage from "../../assets/LoremIpsumGenerator.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function UuidGenerator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();
  const location = useLocation();
  const currentLanguage = getLanguageFromUrl(location.pathname);

  // Function to generate language-specific URLs for tools
  const getToolUrl = (toolKey: string) =>
    getLanguageUrl(currentLanguage, toolKey);

  const handleGenerate = () => {
    dispatch(generateUuidsAsync());
  };

  const handleClear = () => {
    dispatch(clearGeneratedUuids());
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyAllToClipboard = async () => {
    try {
      const allUuids = state.generatedUuids.join('\n');
      await navigator.clipboard.writeText(allUuids);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <SideMenuPageContainer
      pageTitle={t("uuidGenerator.title")}
      pageSeoTitle={t("uuidGenerator.seoTitle")}
      pageDescription={t("uuidGenerator.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("uuidGenerator.mainHeading")}
            </Typography>
          </Grid2>
          
          <Grid2 xs={12}>
            <Typography variant="body1" sx={{ mb: 3, color: "#b8e6f7", opacity: 0.8 }}>
              {t("uuidGenerator.subHeading")}
            </Typography>
          </Grid2>

          <Grid2 xs={12} md={6}>
            <TextField
              fullWidth
              label={t("uuidGenerator.numberOfUuidsLabel")}
              variant="outlined"
              color="info"
              type="number"
              inputProps={{ min: 1, max: 1000 }}
              value={state.numberOfUuids}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value > 0 && value <= 1000) {
                  dispatch(numberOfUuidsChanged(value));
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography>{t("uuidGenerator.uuid")}</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Grid2>

          <Grid2 xs={12} md={6}>
            <Stack 
              direction="row" 
              spacing={2} 
        
            
            >
              <CustomButton
                label={state.isGenerating ? t("common.loading") : t("uuidGenerator.generateButton")}
                onClick={handleGenerate}
                disabled={state.isGenerating}
                width={state.generatedUuids.length > 0 ? 180 : undefined}
              />
              {state.generatedUuids.length > 0 && (
                <CustomButton
                  icon={<RefreshIcon sx={{ color: "#FFD9A8" }} />}
                  label={t("common.clear")}
                  onClick={handleClear}
               
                />
              )}
            </Stack>
          </Grid2>

          {state.generatedUuids.length > 0 && (
            <>
              <Grid2 xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h3" sx={{ color: "#FFE4B5" }}>
                    {t("uuidGenerator.generatedUuids")} ({state.generatedUuids.length})
                  </Typography>
                  <Tooltip title={t("uuidGenerator.copyAllTooltip")}>
                    <IconButton
                      onClick={copyAllToClipboard}
                      sx={{ color: "#b8e6f7" }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid2>

              <Grid2 xs={12}>
                <Stack spacing={2}>
                  {state.generatedUuids.map((uuid, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 2,
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            color: "#FFFFFF",
                            wordBreak: 'break-all',
                            flex: 1,
                            mr: 2,
                          }}
                        >
                          {uuid}
                        </Typography>
                        <Tooltip title={t("uuidGenerator.copyTooltip")}>
                          <IconButton
                            onClick={() => copyToClipboard(uuid)}
                            sx={{ color: "#b8e6f7" }}
                            size="small"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Grid2>
            </>
          )}

          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>

          {/* SEO Content Section */}
          <Grid2 xs={12}>
            <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("uuidGenerator.content.freeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("uuidGenerator.content.freeDescription")}
            </Typography>

            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("uuidGenerator.content.whatIsHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("uuidGenerator.content.whatIsDescription")}
            </Typography>

            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("uuidGenerator.content.useCasesHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("uuidGenerator.content.useCasesDescription")}
            </Typography>

            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("uuidGenerator.content.howToHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("uuidGenerator.content.howToDescription")}
            </Typography>
          </Grid2>

          {/* Related Tools Section */}
          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("uuidGenerator.relatedTools")}
            </Typography>
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="passwordGenerator"
              link={getToolUrl("passwordGenerator")}
              smallCard={true}
              image={PasswordGeneratorImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="loremIpsum"
              link={getToolUrl("loremIpsum")}
              smallCard={true}
              image={LoremIpsumGeneratorImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
